/**
 * Adapter service for emoji-mart v5.
 *
 * Replaces the Angular-provided EmojiSearch and EmojiService from
 * the archived @ctrl/ngx-emoji-mart package.  All emoji-mart v5
 * APIs are accessed through this single service so that the rest
 * of the codebase remains decoupled from the underlying library.
 */

import { Injectable } from '@angular/core';
import data from '@emoji-mart/data/sets/15/google.json';

import { init, SearchIndex } from 'emoji-mart';

import { DEFAULT_QUICK_EMOJI_IDS, seedFrequentEmojiDefaults } from './frequently-used-emojis';

import type { MdwEmoji } from '../models';
import type { EmojiMartData, Emoji as EmojiMartEmoji } from '@emoji-mart/data';

/** Promise that resolves once emoji-mart data is loaded. */
let initPromise: Promise<void> | null = null;

function ensureInit(): Promise<void> {
  if (!initPromise) {
    // Must precede init(): init() synchronously runs FrequentlyUsed.get(), which
    // seeds emoji-mart's OWN defaults into its module-level index when no store
    // exists — and the first FrequentlyUsed.add() would persist them over ours.
    seedFrequentEmojiDefaults(DEFAULT_QUICK_EMOJI_IDS);
    initPromise = init({ data });
  }

  return initPromise;
}

/** Convert an emoji-mart v5 emoji to the internal MdwEmoji format. */
function toMdwEmoji(emoji: EmojiMartEmoji, skinIndex = 0): MdwEmoji {
  const skin = emoji.skins[skinIndex] ?? emoji.skins[0];

  // Custom emojis have `src` on skins but no `unified`/`native`
  if (!skin.unified) {
    return {
      colons: `:${emoji.id}:`,
      id: emoji.id,
      name: emoji.name,
      unified: '',
      native: '',
      imageUrl: (skin as { src?: string }).src,
      custom: true,
    };
  }

  return {
    colons: `:${emoji.id}:`,
    id: emoji.id,
    name: emoji.name,
    unified: skin.unified.toUpperCase(),
    native: skin.native,
    skinVariations:
      emoji.skins.length > 1
        ? emoji.skins.slice(1).map(s => ({
            sheet: [0, 0],
            unified: s.unified.toUpperCase(),
          }))
        : undefined,
  };
}

@Injectable({ providedIn: 'root' })
export class EmojiMartAdapter {
  /** Pre-built lookup maps for synchronous access. */
  private byColons = new Map<string, MdwEmoji>();
  private byNative = new Map<string, MdwEmoji>();
  private emojiData = (data as EmojiMartData).emojis;

  public ready: Promise<void>;

  public constructor() {
    this.ready = ensureInit();
    this.buildLookupMaps();
  }

  // ---------------------------------------------------------------------------
  // Search
  // ---------------------------------------------------------------------------

  /** Async search wrapping emoji-mart v5 SearchIndex. */
  public async search(query: string): Promise<MdwEmoji[]> {
    await this.ready;
    const results: EmojiMartEmoji[] = (await SearchIndex.search(query)) ?? [];

    return results.map(e => toMdwEmoji(e));
  }

  // ---------------------------------------------------------------------------
  // Synchronous lookups (from pre-built maps)
  // ---------------------------------------------------------------------------

  /** All emojis as MdwEmoji keyed by `:id:` colons notation. */
  public get emojisList(): Map<string, MdwEmoji> {
    return this.byColons;
  }

  /** Find an emoji by its colons notation (e.g. `:grinning:`). */
  public getEmojiByColons(colons: string): MdwEmoji | undefined {
    return this.byColons.get(colons);
  }

  /** Find an emoji by its native unicode character. */
  public getEmojiByNative(native: string): MdwEmoji | undefined {
    return this.byNative.get(native);
  }

  // ---------------------------------------------------------------------------
  // Skin tone variants
  // ---------------------------------------------------------------------------

  /**
   * Returns the native emoji character with the given skin tone applied.
   * Replaces `EmojiService.getSanitizedData(id, skin, set)` from the old library.
   *
   * @param id Emoji id (shortcode without colons), e.g. `"+1"` or `"wave"`
   * @param skinTone 1-6 where 1 = default (no modifier)
   * @returns Object with id and native string, or undefined if not found
   */
  public getSkinVariant(
    id: string,
    skinTone: 1 | 2 | 3 | 4 | 5 | 6,
  ): { id: string; native: string; colons: string } | undefined {
    const emoji = this.emojiData[id];

    if (!emoji) {
      return undefined;
    }

    const skinIndex = skinTone - 1;
    const skin = emoji.skins[skinIndex] ?? emoji.skins[0];

    if (!skin) {
      return undefined;
    }

    const colonsSuffix = skinTone > 1 ? `:skin-tone-${skinTone}:` : '';

    return {
      id: emoji.id,
      native: skin.native,
      colons: `:${emoji.id}:${colonsSuffix}`,
    };
  }

  // ---------------------------------------------------------------------------
  // Internal
  // ---------------------------------------------------------------------------

  private buildLookupMaps() {
    for (const [, emoji] of Object.entries(this.emojiData) as [string, EmojiMartEmoji][]) {
      const mdw = toMdwEmoji(emoji);

      this.byColons.set(mdw.colons, mdw);

      if (mdw.native) {
        this.byNative.set(mdw.native, mdw);
      }
    }
  }
}
